import { DEV_PASSWORD } from "@lms/backend/lib/auth/password-policy";
import { isDeployable } from "@lms/backend/lib/deploy/deployable";

/**
 * Sample seed logins offered as one-click buttons on the login screen.
 *
 * These are the accounts created by `backend/scripts/seed.ts` with the shared
 * development password. They are a local convenience: a production build shows
 * none of them unless the deploy opts in explicitly (`VITE_DEMO_LOGINS=true`), and
 * even then the tenant admin needs its own opt-in (`VITE_DEMO_ADMIN_LOGIN=true`) —
 * SEC-2. Unset, "false" or anything other than "true" means off, for every tenant.
 *
 * Never add an account here that is not created by the seed with DEV_PASSWORD.
 * A real credential in this table would ship to every client that loads the page.
 */
export type DevLoginAccount = {
  /** Human label for the button. */
  label: string;
  /** Role shown alongside the label, so the tester knows what they are getting. */
  role: string;
  email: string;
  password: string;
};

type DevLoginOptions = {
  isProduction: boolean;
  /** When true in production, expose seeded one-click logins (explicit opt-in only). */
  demoLoginsEnabled?: boolean;
  /**
   * Whether the tenant admin button is offered. Defaults to on outside production and off in
   * production: its password is public, and it can change the tenant's email settings, members and
   * branding (SEC-2).
   */
  includeTenantAdmin?: boolean;
};

/**
 * The roles a production demo may offer. MT-1 AUTH-009 S14: no list offers a platform admin — the
 * platform console has its own sign-in at /platform/login.
 */
const PRODUCTION_DEMO_ROLES = new Set(["student", "instructor", "tenant_admin"]);

function isExplicitTrue(value: string | undefined): boolean {
  return value?.trim().toLowerCase() === "true";
}

/** Backend `DEMO_LOGINS`: on only when explicitly "true" — never by tenant slug (SEC-2). */
export function isDemoLoginsEnabled(
  env: Record<string, string | undefined> = process.env,
): boolean {
  return isExplicitTrue(env.DEMO_LOGINS);
}

/**
 * AUTH-9: the seeded demo accounts have no real inbox, so with email sign-in codes on they could
 * never finish signing in. They skip the code step — only when demo logins are explicitly enabled
 * (`DEMO_LOGINS=true`) and never on a deployable server, where every account gets a code.
 */
export function demoAccountSkipsEmailCodes(
  email: string,
  env: Record<string, string | undefined> = process.env,
): boolean {
  // S3 V-7: "deployable" (NODE_ENV=production or DEPLOYABLE=true), as every sibling guard.
  if (!isDemoLoginsEnabled(env) || isDeployable(env)) return false;
  const normalized = email.trim().toLowerCase();
  return Object.values(SEEDED_ACCOUNTS).some((accounts) =>
    accounts.some((account) => account.email === normalized),
  );
}

/**
 * The login page's demo flags from the build env (`VITE_DEMO_LOGINS`, `VITE_DEMO_ADMIN_LOGIN`).
 * Production builds need an explicit "true" for each; local dev keeps the buttons (and the
 * admin one unless it is switched off with "false").
 */
export function demoLoginFlags(input: {
  isProduction: boolean;
  demoLogins?: string;
  demoAdminLogin?: string;
}): { demoLoginsEnabled: boolean; includeTenantAdmin: boolean } {
  if (!input.isProduction) {
    return {
      demoLoginsEnabled: false,
      includeTenantAdmin: input.demoAdminLogin?.trim().toLowerCase() !== "false",
    };
  }
  return {
    demoLoginsEnabled: isExplicitTrue(input.demoLogins),
    includeTenantAdmin: isExplicitTrue(input.demoAdminLogin),
  };
}

const SEEDED_ACCOUNTS: Record<string, ReadonlyArray<Omit<DevLoginAccount, "password">>> = {
  astromania: [
    { label: "Astromania Student", role: "student", email: "student@astromania.test" },
    { label: "Astromania Instructor", role: "instructor", email: "instructor@astromania.test" },
    { label: "Astromania Admin", role: "tenant_admin", email: "admin@astromania.test" },
  ],
  "tenant-b": [{ label: "Tenant B Student", role: "student", email: "student@tenant-b.test" }],
};

/**
 * Seeded sample logins for a tenant, or an empty list in production or for a
 * tenant the seed does not create.
 */
export function devLoginAccountsFor(
  tenantSlug: string,
  { isProduction, demoLoginsEnabled = false, includeTenantAdmin = !isProduction }: DevLoginOptions,
): DevLoginAccount[] {
  if (isProduction && !demoLoginsEnabled) return [];

  const accounts = SEEDED_ACCOUNTS[tenantSlug];
  if (!accounts) return [];

  const visible = (
    isProduction ? accounts.filter((account) => PRODUCTION_DEMO_ROLES.has(account.role)) : accounts
  ).filter((account) => includeTenantAdmin || account.role !== "tenant_admin");

  return visible.map((account) => ({ ...account, password: DEV_PASSWORD }));
}
